import { Flex, Icon, Text, TooltipProps } from "@chakra-ui/react";
import {
  decimalToPercentage,
  isDefined,
  toNumberDisplayValue,
} from "@intentsify/utils";
import { Card, CardProps } from "components";
import { IconType } from "react-icons";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { byLook } from "theme/look";
import { LegacyStatCard } from "./legacy/StatCard";
import { getArrowType } from "./utils";

export type StatCardProps<
  T extends number | undefined | null = number | undefined | null
> = {
  title: string;
  description?: string;
  caption?: string;
  titleTooltip?: string;
  titleTooltipPlacement?: TooltipProps["placement"];
  value: T;
  valueFormatter?: (value: number) => string;
  previousPeriodChange?: T;
  previousPeriodLabel?: string;
  colorScheme?: CardProps["colorScheme"];
  isLoading?: boolean;
  hasData?: boolean;
  noDataMessage?: string;
  noDataIcon?: IconType;
  noValueMessage?: string;
  /** When provided, the card becomes clickable (handled by StatCardsContainer). */
  onClick?: () => void;
  /** Highlights the card as the active selection (handled by StatCardsContainer). */
  isActive?: boolean;
};

const BrandStatCard = <
  T extends number | undefined | null = number | undefined | null
>({
  value,
  title,
  description,
  caption,
  valueFormatter = toNumberDisplayValue,
  previousPeriodChange,
  previousPeriodLabel = "vs previous period",
  titleTooltip,
  titleTooltipPlacement,
  colorScheme = "white",
  isLoading = false,
  hasData = true,
  noDataMessage,
  noDataIcon,
  noValueMessage = "n/a",
}: StatCardProps<T>) => {
  const arrowType = getArrowType(previousPeriodChange);
  // The pill comes first ("▲ 6.12% vs previous period"), so drop the colon
  // labels were written with when they preceded the value.
  const comparisonLabel = previousPeriodLabel.replace(/:\s*$/, "");

  return (
    <Card
      variant="tile"
      title={title}
      titleTooltip={titleTooltip}
      titleTooltipPlacement={titleTooltipPlacement}
      isLoading={isLoading}
      colorScheme={colorScheme}
      hasData={hasData}
      noDataMessage={noDataMessage}
      noDataIcon={noDataIcon}
      ignoreTitleMargin
    >
      <Flex flexGrow={1} flexDir="column" justifyContent="center" py={2}>
        {value || value === 0 ? (
          <Text
            fontSize="4xl"
            fontWeight="medium"
            letterSpacing="-0.02em"
            lineHeight="1.1"
          >
            {valueFormatter(value)}
          </Text>
        ) : (
          // Fallbacks can be sentences ("No historical data available."), so
          // they read as a muted note rather than a headline figure.
          <Text
            fontSize={noValueMessage.length > 4 ? "md" : "4xl"}
            fontWeight="medium"
            lineHeight="1.3"
            color="gray.500"
            _dark={{ color: "gray.400" }}
          >
            {noValueMessage}
          </Text>
        )}

        {description && (
          <Text fontSize="sm" fontWeight="semibold" mt={1}>
            {description}
          </Text>
        )}

        {caption && (
          <Text fontSize="sm" color="gray.500" _dark={{ color: "gray.400" }}>
            {caption}
          </Text>
        )}
      </Flex>

      <Flex minH={5} alignItems="center" gap={2} fontSize="xs">
        {isDefined(previousPeriodChange) && (
          <>
            <DeltaPill
              arrowType={arrowType}
              change={previousPeriodChange ?? 0}
            />
            <Text as="span" color="gray.500" _dark={{ color: "gray.400" }}>
              {comparisonLabel}
            </Text>
          </>
        )}
      </Flex>
    </Card>
  );
};

const deltaStyles = {
  increase: {
    icon: FiChevronUp,
    bg: "teal.50",
    color: "teal.700",
    _dark: { bg: "rgba(35, 164, 130, 0.16)", color: "teal.300" },
  },
  decrease: {
    icon: FiChevronDown,
    bg: "red.50",
    color: "red.600",
    _dark: { bg: "rgba(214, 69, 69, 0.18)", color: "red.300" },
  },
};

const DeltaPill = ({
  arrowType,
  change,
}: {
  arrowType: ReturnType<typeof getArrowType>;
  change: number;
}) => {
  if (!arrowType) {
    return (
      <Text as="span" fontWeight="semibold" color="gray.500">
        – 0%
      </Text>
    );
  }

  const { icon, ...style } = deltaStyles[arrowType];

  return (
    <Flex
      as="span"
      alignItems="center"
      gap={0.5}
      px={1.5}
      py={0.5}
      borderRadius="sm"
      fontWeight="semibold"
      {...style}
    >
      <Icon as={icon} w={3.5} h={3.5} />
      {toNumberDisplayValue(decimalToPercentage(change))}%
    </Flex>
  );
};

const StatCard = byLook(BrandStatCard, LegacyStatCard);

export { StatCard };
